<template>
    <div class="google-business-reviews-page">
        <!-- Left Panel: Reviews -->
        <div class="reviews-panel">
            <div class="panel-header">
                <h2 class="panel-title">
                    <span class="material-icons google-icon">star</span>
                    {{ $t("googleBusiness.reviews") }}
                    <span class="count-badge">{{ total }}</span>
                </h2>
                <div class="header-actions">
                    <a-select
                        v-model="filters.locationId"
                        :placeholder="$t('googleBusiness.selectLocation')"
                        style="width: 200px"
                        size="small"
                        allowClear
                        showSearch
                        :filterOption="filterLocationOption"
                    >
                        <a-select-option value="">{{ $t("googleBusiness.allLocations") }}</a-select-option>
                        <a-select-option v-for="location in locations" :key="location.locationId" :value="location.locationId">
                            {{ location.name | truncate(40) }}
                        </a-select-option>
                    </a-select>
                    <a-select v-model="filters.status" style="width: 120px" size="small">
                        <a-select-option value="">{{ $t("googleBusiness.all") }}</a-select-option>
                        <a-select-option value="new">{{ $t("googleBusiness.new") }}</a-select-option>
                        <a-select-option value="replied">{{ $t("googleBusiness.replied") }}</a-select-option>
                        <a-select-option value="resolved">{{ $t("googleBusiness.resolved") }}</a-select-option>
                    </a-select>
                    <a-select v-model="filters.starRating" style="width: 100px" size="small">
                        <a-select-option value="">{{ $t("googleBusiness.allRatings") }}</a-select-option>
                        <a-select-option value="FIVE">5 Stars</a-select-option>
                        <a-select-option value="FOUR">4 Stars</a-select-option>
                        <a-select-option value="THREE">3 Stars</a-select-option>
                        <a-select-option value="TWO">2 Stars</a-select-option>
                        <a-select-option value="ONE">1 Star</a-select-option>
                    </a-select>
                    <a-button size="small" @click="fetchReviews" :loading="loading">
                        <a-icon type="reload" />
                    </a-button>
                </div>
            </div>

            <div class="reviews-list" v-if="!loading">
                <div
                    class="review-item"
                    v-for="review in reviews"
                    :key="review._id"
                    :class="{ selected: selectedReview && selectedReview._id === review._id }"
                    @click="selectReview(review)"
                >
                    <img :src="review.reviewer.profilePhotoUrl" class="author-avatar" v-if="review.reviewer.profilePhotoUrl" />
                    <span class="material-icons avatar-placeholder" v-else>account_circle</span>
                    <div class="review-main">
                        <div class="review-top">
                            <span class="author-name">{{ review.reviewer.displayName }}</span>
                            <span class="review-date">{{ formatDate(review.createTime) }}</span>
                            <span class="status-dot" :class="review.status" :title="getStatusLabel(review.status)"></span>
                        </div>
                        <div class="star-rating">
                            <span class="material-icons" v-for="n in getStarCount(review.starRating)" :key="'filled-' + n">star</span>
                            <span class="material-icons empty" v-for="n in (5 - getStarCount(review.starRating))" :key="'empty-' + n">star_border</span>
                        </div>
                        <p class="review-text" v-if="review.comment">{{ truncateText(review.comment, 120) }}</p>
                        <p class="review-text no-comment" v-else>{{ $t("googleBusiness.noComment") }}</p>
                        <div class="review-bottom" v-if="review.location">
                            <span class="location-link">
                                <span class="material-icons">store</span> {{ review.location.name | truncate(25) }}
                            </span>
                        </div>
                    </div>
                    <div class="review-actions-mini">
                        <a-dropdown :trigger="['click']" @click.stop>
                            <a-button size="small" type="link"><a-icon type="more" /></a-button>
                            <template #overlay>
                                <a-menu>
                                    <a-sub-menu key="status" :title="$t('googleBusiness.status')">
                                        <a-menu-item key="new" @click="updateStatus(review, 'new')">
                                            <span class="status-indicator new"></span> {{ $t("googleBusiness.new") }}
                                        </a-menu-item>
                                        <a-menu-item key="replied" @click="updateStatus(review, 'replied')">
                                            <span class="status-indicator replied"></span> {{ $t("googleBusiness.replied") }}
                                        </a-menu-item>
                                        <a-menu-item key="resolved" @click="updateStatus(review, 'resolved')">
                                            <span class="status-indicator resolved"></span> {{ $t("googleBusiness.resolved") }}
                                        </a-menu-item>
                                    </a-sub-menu>
                                    <a-menu-divider />
                                    <a-menu-item key="reply" @click="openReplyModal(review)">
                                        <a-icon type="message" /> {{ $t("googleBusiness.reply") }}
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                    </div>
                </div>

                <div class="empty-state" v-if="reviews.length === 0">
                    <span class="material-icons">reviews</span>
                    <p>{{ $t("googleBusiness.noReviewsFound") }}</p>
                </div>
            </div>

            <div class="loading-state" v-else>
                <a-spin />
            </div>

            <div class="pagination-wrapper" v-if="total > pagination.limit">
                <a-pagination
                    :current="pagination.page"
                    :total="total"
                    :page-size="pagination.limit"
                    size="small"
                    @change="onPageChange"
                    @showSizeChange="onPageSizeChange"
                    show-size-changer
                    :page-size-options="['20', '50', '100']"
                />
            </div>
        </div>

        <!-- Right Panel: Review Detail -->
        <div class="detail-panel" v-if="selectedReview">
            <div class="detail-header">
                <img :src="selectedReview.reviewer.profilePhotoUrl" v-if="selectedReview.reviewer.profilePhotoUrl" />
                <span class="material-icons" v-else>account_circle</span>
                <div class="author-details">
                    <span class="name">{{ selectedReview.reviewer.displayName }}</span>
                    <span class="date">{{ formatDate(selectedReview.createTime) }}</span>
                </div>
                <div class="badges">
                    <span class="status-badge" :class="selectedReview.status">{{ getStatusLabel(selectedReview.status) }}</span>
                </div>
            </div>

            <div class="star-rating-large">
                <span class="material-icons" v-for="n in getStarCount(selectedReview.starRating)" :key="'filled-' + n">star</span>
                <span class="material-icons empty" v-for="n in (5 - getStarCount(selectedReview.starRating))" :key="'empty-' + n">star_border</span>
            </div>

            <div class="detail-body" v-if="selectedReview.comment">{{ selectedReview.comment }}</div>
            <div class="detail-body no-comment" v-else>{{ $t("googleBusiness.noComment") }}</div>

            <div class="reply-section" v-if="selectedReview.reply && selectedReview.reply.comment">
                <div class="reply-header">
                    <span class="material-icons">reply</span>
                    {{ $t("googleBusiness.yourReply") }}
                </div>
                <div class="reply-content">{{ selectedReview.reply.comment }}</div>
                <div class="reply-date">{{ formatDate(selectedReview.reply.updateTime) }}</div>
            </div>

            <div class="detail-actions">
                <a-button size="small" type="primary" @click="openReplyModal(selectedReview)">
                    <a-icon type="message" /> {{ selectedReview.reply ? $t("googleBusiness.updateReply") : $t("googleBusiness.reply") }}
                </a-button>
                <a-dropdown :trigger="['click']">
                    <a-button size="small">{{ $t("googleBusiness.status") }} <a-icon type="down" /></a-button>
                    <template #overlay>
                        <a-menu @click="(e) => updateStatus(selectedReview, e.key)">
                            <a-menu-item key="new">{{ $t("googleBusiness.new") }}</a-menu-item>
                            <a-menu-item key="replied">{{ $t("googleBusiness.replied") }}</a-menu-item>
                            <a-menu-item key="resolved">{{ $t("googleBusiness.resolved") }}</a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
            </div>
        </div>

        <!-- Empty Detail Panel -->
        <div class="detail-panel empty" v-else>
            <span class="material-icons">rate_review</span>
            <p>{{ $t("googleBusiness.selectReviewToPreview") }}</p>
        </div>

        <!-- Reply Modal -->
        <a-modal
            v-model="replyModal.visible"
            :title="$t('googleBusiness.replyToReview')"
            @ok="submitReply"
            :confirm-loading="replyModal.loading"
            width="500px"
        >
            <div class="reply-preview" v-if="replyModal.review">
                <img :src="replyModal.review.reviewer.profilePhotoUrl" v-if="replyModal.review.reviewer.profilePhotoUrl" />
                <div>
                    <strong>{{ replyModal.review.reviewer.displayName }}</strong>
                    <div class="star-rating-small">
                        <span class="material-icons" v-for="n in getStarCount(replyModal.review.starRating)" :key="'filled-' + n">star</span>
                    </div>
                    <p v-if="replyModal.review.comment">{{ truncateText(replyModal.review.comment, 200) }}</p>
                </div>
            </div>
            <a-textarea
                v-model="replyModal.text"
                :placeholder="$t('googleBusiness.writeYourReply')"
                :rows="4"
                :maxLength="4096"
            />
        </a-modal>
    </div>
</template>

<script>
import { GOOGLE_BUSINESS_API } from "../../../../../api/google-business";
import moment from "moment";

export default {
    name: "ReviewList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    filters: {
        truncate(value, length) {
            if (!value) return "";
            if (value.length <= length) return value;
            return value.substring(0, length) + "...";
        },
    },
    data() {
        return {
            reviews: [],
            locations: [],
            loading: false,
            total: 0,
            selectedReview: null,
            filters: {
                status: "",
                starRating: "",
                locationId: "",
            },
            pagination: {
                page: 1,
                limit: 20,
            },
            replyModal: {
                visible: false,
                review: null,
                text: "",
                loading: false,
            },
        };
    },
    methods: {
        getStatusLabel(status) {
            var labels = {
                new: this.$t("googleBusiness.new"),
                replied: this.$t("googleBusiness.replied"),
                resolved: this.$t("googleBusiness.resolved"),
            };
            return labels[status] || status;
        },
        getStarCount(rating) {
            var map = {
                ONE: 1,
                TWO: 2,
                THREE: 3,
                FOUR: 4,
                FIVE: 5,
            };
            return map[rating] || 0;
        },
        async fetchLocations() {
            try {
                const response = await GOOGLE_BUSINESS_API.listLocations(this.channel._id, 1, 100);
                this.locations = response.data.locations || [];
            } catch (err) {
                console.error("Error fetching locations", err);
            }
        },
        async fetchReviews() {
            this.loading = true;
            try {
                const response = await GOOGLE_BUSINESS_API.listReviews({
                    channelId: this.channel._id,
                    locationId: this.filters.locationId || undefined,
                    status: this.filters.status || undefined,
                    starRating: this.filters.starRating || undefined,
                    page: this.pagination.page,
                    limit: this.pagination.limit,
                });
                this.reviews = response.data.reviews;
                this.total = response.data.total;
            } catch (err) {
                this.$message.error(this.$t("googleBusiness.errorFetchingReviews"));
            } finally {
                this.loading = false;
            }
        },
        selectReview(review) {
            this.selectedReview = review;
        },
        async updateStatus(review, status) {
            try {
                await GOOGLE_BUSINESS_API.updateStatus(review._id, status);
                review.status = status;
                if (this.selectedReview && this.selectedReview._id === review._id) {
                    this.selectedReview.status = status;
                }
                this.$message.success(this.$t("googleBusiness.statusUpdated"));
            } catch (err) {
                this.$message.error(this.$t("googleBusiness.errorUpdatingStatus"));
            }
        },
        openReplyModal(review) {
            this.replyModal.review = review;
            this.replyModal.text = review.reply ? review.reply.comment : "";
            this.replyModal.visible = true;
        },
        async submitReply() {
            if (!this.replyModal.text.trim()) {
                this.$message.warning(this.$t("googleBusiness.pleaseEnterReply"));
                return;
            }
            this.replyModal.loading = true;
            try {
                await GOOGLE_BUSINESS_API.replyReview(this.replyModal.review._id, {
                    text: this.replyModal.text,
                    channelId: this.channel._id,
                });
                this.replyModal.review.status = "replied";
                this.replyModal.review.reply = {
                    comment: this.replyModal.text,
                    updateTime: new Date(),
                };
                if (this.selectedReview && this.selectedReview._id === this.replyModal.review._id) {
                    this.selectedReview.status = "replied";
                    this.selectedReview.reply = this.replyModal.review.reply;
                }
                this.$message.success(this.$t("googleBusiness.replySuccess"));
                this.replyModal.visible = false;
            } catch (err) {
                this.$message.error(this.$t("googleBusiness.errorSendingReply"));
            } finally {
                this.replyModal.loading = false;
            }
        },
        onPageChange(page) {
            this.pagination.page = page;
            this.fetchReviews();
        },
        onPageSizeChange(current, size) {
            this.pagination.limit = size;
            this.pagination.page = 1;
            this.fetchReviews();
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        truncateText(text, length) {
            if (!text) return "";
            if (text.length <= length) return text;
            return text.substring(0, length) + "...";
        },
        filterLocationOption(input, option) {
            var name = option.componentOptions.children[0].text || "";
            return name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
    },
    watch: {
        "filters.status"() {
            this.pagination.page = 1;
            this.fetchReviews();
        },
        "filters.locationId"() {
            this.pagination.page = 1;
            this.fetchReviews();
        },
        "filters.starRating"() {
            this.pagination.page = 1;
            this.fetchReviews();
        },
    },
    mounted() {
        this.fetchLocations();
        this.fetchReviews();
    },
};
</script>

<style lang="scss" >
.google-business-reviews-page {
    display: flex;
    gap: 20px;
    height: calc(100vh - 200px);
    padding: 16px;

    .reviews-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: var(--color-bg-default);
        border-radius: 12px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;

        .panel-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            border-bottom: 1px solid var(--color-border-default);
            flex-wrap: wrap;
            gap: 12px;

            .panel-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 16px;
                font-weight: 600;
                margin: 0;

                .google-icon {
                    color: #fbbc05;
                    font-size: 20px;
                }

                .count-badge {
                    background: var(--color-bg-subtle);
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
            }

            .header-actions {
                display: flex;
                gap: 8px;
                align-items: center;
            }
        }

        .reviews-list {
            flex: 1;
            overflow-y: auto;
            padding: 8px;

            .review-item {
                display: flex;
                gap: 12px;
                padding: 12px;
                border-radius: 8px;
                cursor: pointer;
                transition: background 0.15s;
                border: 1px solid transparent;

                &:hover {
                    background: var(--color-bg-subtle);
                }

                &.selected {
                    background: var(--color-bg-subtle);
                    border-color: var(--color-border-strong);
                }

                .author-avatar {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }

                .avatar-placeholder {
                    font-size: 36px;
                    color: var(--color-text-default);
                    flex-shrink: 0;
                }

                .review-main {
                    flex: 1;
                    min-width: 0;

                    .review-top {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 4px;

                        .author-name {
                            font-weight: 600;
                            font-size: 13px;
                        }

                        .review-date {
                            font-size: 11px;
                            color: var(--color-text-default);
                        }

                        .status-dot {
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            margin-left: auto;

                            &.new { background: #1890ff; }
                            &.replied { background: #52c41a; }
                            &.resolved { background: #8c8c8c; }
                        }
                    }

                    .star-rating {
                        margin-bottom: 6px;

                        .material-icons {
                            font-size: 14px;
                            color: #fbbc05;

                            &.empty {
                                color: #e0e0e0;
                            }
                        }
                    }

                    .review-text {
                        font-size: 13px;
                        line-height: 1.5;
                        margin: 0 0 6px;
                        color: var(--color-text-strong-default);

                        &.no-comment {
                            font-style: italic;
                            color: var(--color-text-default);
                        }
                    }

                    .review-bottom {
                        display: flex;
                        gap: 12px;
                        font-size: 11px;
                        color: var(--color-text-default);

                        .location-link {
                            display: flex;
                            align-items: center;
                            gap: 4px;

                            .material-icons {
                                font-size: 14px;
                            }
                        }
                    }
                }

                .review-actions-mini {
                    opacity: 0;
                    transition: opacity 0.15s;
                }

                &:hover .review-actions-mini {
                    opacity: 1;
                }
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 60px 20px;
                color: var(--color-text-default);

                .material-icons {
                    font-size: 48px;
                    margin-bottom: 12px;
                    opacity: 0.5;
                }
            }
        }

        .loading-state {
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 1;
        }

        .pagination-wrapper {
            padding: 12px 16px;
            border-top: 1px solid var(--color-border-default);
            display: flex;
            justify-content: center;
        }
    }

    .detail-panel {
        width: 380px;
        flex-shrink: 0;
        background: var(--color-bg-default);
        border-radius: 12px;
        border: 1px solid var(--color-border-default);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 16px;

        &.empty {
            justify-content: center;
            align-items: center;
            color: var(--color-text-default);

            .material-icons {
                font-size: 48px;
                margin-bottom: 12px;
                opacity: 0.4;
            }

            p {
                font-size: 14px;
                margin: 0;
            }
        }

        .detail-header {
            display: flex;
            gap: 10px;
            margin-bottom: 12px;

            img {
                width: 40px;
                height: 40px;
                border-radius: 50%;
            }

            .material-icons {
                font-size: 40px;
                color: var(--color-text-default);
            }

            .author-details {
                flex: 1;

                .name {
                    display: block;
                    font-weight: 600;
                    font-size: 14px;
                }

                .date {
                    font-size: 12px;
                    color: var(--color-text-default);
                }
            }

            .badges {
                .status-badge {
                    padding: 2px 8px;
                    border-radius: 10px;
                    font-size: 10px;

                    &.new { background: #e6f7ff; color: #1890ff; }
                    &.replied { background: #f6ffed; color: #52c41a; }
                    &.resolved { background: #f5f5f5; color: #8c8c8c; }
                }
            }
        }

        .star-rating-large {
            margin-bottom: 12px;

            .material-icons {
                font-size: 24px;
                color: #fbbc05;

                &.empty {
                    color: #e0e0e0;
                }
            }
        }

        .detail-body {
            font-size: 14px;
            line-height: 1.6;
            margin-bottom: 16px;
            padding: 12px;
            background: var(--color-bg-subtle);
            border-radius: 8px;

            &.no-comment {
                font-style: italic;
                color: var(--color-text-default);
            }
        }

        .reply-section {
            margin-bottom: 16px;
            padding: 12px;
            background: #f6ffed;
            border-radius: 8px;
            border-left: 3px solid #52c41a;

            .reply-header {
                display: flex;
                align-items: center;
                gap: 4px;
                font-weight: 600;
                font-size: 12px;
                color: #52c41a;
                margin-bottom: 8px;

                .material-icons {
                    font-size: 16px;
                }
            }

            .reply-content {
                font-size: 13px;
                line-height: 1.5;
            }

            .reply-date {
                font-size: 11px;
                color: var(--color-text-default);
                margin-top: 8px;
            }
        }

        .detail-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            margin-top: auto;
        }
    }

    .reply-preview {
        display: flex;
        gap: 12px;
        padding: 12px;
        background: var(--color-bg-subtle);
        border-radius: 8px;
        margin-bottom: 16px;

        img {
            width: 32px;
            height: 32px;
            border-radius: 50%;
        }

        .star-rating-small {
            .material-icons {
                font-size: 12px;
                color: #fbbc05;
            }
        }

        p {
            margin: 4px 0 0;
            font-size: 13px;
            color: var(--color-text-default);
        }
    }

    .status-indicator {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;

        &.new { background: #1890ff; }
        &.replied { background: #52c41a; }
        &.resolved { background: #8c8c8c; }
    }
}
</style>
